import axiosInstance from "../../api/axios";

const IncomeService = {
    getAllIncomes: async () => {
        const response = await axiosInstance.get("/income");
        return response.data;
    },
    getUnifiedIncomes: async () => {
        const response = await axiosInstance.get("/income/unified");
        return response.data;
    },
    getYoutubeIncomeDetails: async ({ type, id }) => {
        const response = await axiosInstance.get(`/income/youtube/${type}/${id}`);
        return response.data;
    },
    getYoutubeRevenueUrls: async () => {
        const response = await axiosInstance.get("/income/youtube/revenue-urls");
        return response.data;
    },
    addIncome: async (incomeData) => {
        const response = await axiosInstance.post("/income", incomeData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    },
    deleteIncome: async ({
        id,
        entity_id,
        source_type,
        youtube_type,
        delete_related_data,
    }) => {
        const deleteRelatedData = delete_related_data === true;

        try {
            // YouTube channel revenue lives in artists_revenue, not incomes.
            if (source_type === "youtube" && youtube_type === "artists_revenue") {
                const ytId =
                    entity_id ??
                    String(id ?? "").replace(/^youtube-artist-/i, "");
                const response = await axiosInstance.delete(
                    `/income/youtube/artists_revenue/${ytId}`,
                    {
                        data: { delete_related_data: deleteRelatedData },
                        timeout: 300000,
                    }
                );
                return response.data;
            }

            if (source_type === "youtube" && youtube_type === "income") {
                const ytIncomeId = entity_id ?? id;
                const response = await axiosInstance.delete(
                    `/income/youtube/income/${ytIncomeId}`,
                    {
                        data: { delete_related_data: deleteRelatedData },
                        timeout: 300000,
                    }
                );
                return response.data;
            }

            const response = await axiosInstance.delete(`/income/${id}`, {
                data: { delete_related_data: deleteRelatedData },
                timeout: 300000,
            });
            return response.data;
        } catch (error) {
            console.error('Delete income error:', error.response?.data || error.message);
            throw error;
        }
    },
    updateIncome: async (id, incomeData) => {
        const response = await axiosInstance.post(`/income/${id}`, incomeData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    },
    getIncomeById: async (id) => {
        const response = await axiosInstance.get(`/income/${id}`);
        return response.data;
    },
    uploadRawFile: async (id, file, { onProgress, replaceExisting = false } = {}) => {
        const extension = file.name.split(".").pop()?.toLowerCase() || "csv";
        const fileTypeMap = { csv: "csv", xlsx: "xlsx", xls: "xls", tsv: "tsv", txt: "csv" };
        const fileType = fileTypeMap[extension] || "csv";

        const presignedResponse = await axiosInstance.post(
            `/incomes/s3/presigned/${id}`,
            {
                file_name: file.name,
                file_size: file.size,
                file_type: fileType,
                replace_existing: replaceExisting,
            },
            {
                headers: { "Content-Type": "application/json" },
                timeout: 30000,
            }
        );

        if (!presignedResponse.data?.success) {
            throw new Error(presignedResponse.data?.message || "Failed to get upload URL");
        }

        const { presigned_url: presignedUrl, s3_key: s3Key, content_type: contentType, file_name: fileName } =
            presignedResponse.data;

        await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            if (onProgress) {
                xhr.upload.addEventListener("progress", (event) => {
                    if (event.lengthComputable) {
                        onProgress(Math.round((event.loaded / event.total) * 100));
                    }
                });
            }

            xhr.addEventListener("load", () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                    return;
                }
                reject(new Error(`S3 upload failed with status ${xhr.status}`));
            });

            xhr.addEventListener("error", () => reject(new Error("S3 upload network error")));
            xhr.addEventListener("abort", () => reject(new Error("S3 upload aborted")));

            xhr.open("PUT", presignedUrl);
            xhr.setRequestHeader("Content-Type", contentType || file.type || "application/octet-stream");
            xhr.send(file);
        });

        const completeResponse = await axiosInstance.post(
            "/incomes/s3/upload-complete",
            {
                s3_key: s3Key,
                file_name: fileName || file.name,
                file_size: file.size,
                upload_status: "success",
                income_id: id,
            },
            {
                headers: { "Content-Type": "application/json" },
                timeout: 30000,
            }
        );

        if (!completeResponse.data?.success) {
            throw new Error(completeResponse.data?.message || "Failed to register upload");
        }

        return completeResponse.data;
    },
    updateYoutubeIncome: async ({ type, id, payload }) => {
        const response = await axiosInstance.patch(`/income/youtube/${type}/${id}`, payload);
        return response.data;
    },
    downloadFile: async (id) => {
        const response = await axiosInstance.get(`/income/download/${id}`, {
            responseType: 'blob',
        });
        const contentDisposition = response.headers['content-disposition'];
        const fileName = contentDisposition ? contentDisposition.split('filename=')[1].replace(/"/g, '') : 'downloaded_file';
        const mimeType = response.headers['content-type'];
        return { data: response.data, fileName, mimeType };
    },

    downloadrowFile: async (id) => {
        try {
            const url = `/income/downloadraw/${id}`;
            console.log('Calling downloadrowFile API for ID:', id);
            console.log('Full API URL:', url);

            const response = await axiosInstance.get(url, {
                responseType: 'blob',
            });
            console.log('API response status:', response.status);
            console.log('API response headers:', response.headers);

            const contentDisposition = response.headers['content-disposition'];
            console.log('Content-Disposition header:', contentDisposition);

            const fileName = contentDisposition ? contentDisposition.split('filename=')[1].replace(/"/g, '') : 'downloaded_file.csv';
            const mimeType = response.headers['content-type'] || 'application/octet-stream';

            console.log('Extracted fileName:', fileName);
            console.log('Extracted mimeType:', mimeType);

            return { data: response.data, fileName, mimeType };
        } catch (error) {
            console.error('Error in downloadrowFile:', error);
            throw error; // Re-throw to be handled by the caller
        }
    },

    getProviders: async () => {
        const response = await axiosInstance.get('/providers');
        return response.data.providers;
    },

    processCsvS3: async (incomeId, options = {}) => {
        const url = `/incomes/process-csv-s3/${incomeId}`;
        const data = {
            delimiter: options.delimiter ?? ',',
            auto_detect_delimiter: options.auto_detect_delimiter ?? true,
            force_reprocess: options.force_reprocess ?? false,
        };

        const response = await axiosInstance.post(url, data, {
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 600000,
        });

        return response.data;
    },

    retryLambda: async (incomeId) => {
        const response = await axiosInstance.post(`/incomes/retry-lambda/${incomeId}`);
        return response.data;
    },
};

export default IncomeService;